import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const routerBasename =
  process.env.PUBLIC_URL && process.env.PUBLIC_URL !== '/'
    ? process.env.PUBLIC_URL.replace(/\/$/, '')
    : '';

function stripBasename(pathname: string): string {
  if (!routerBasename) return pathname;
  if (pathname === routerBasename || pathname.startsWith(`${routerBasename}/`)) {
    const rest = pathname.slice(routerBasename.length) || '/';
    return rest.startsWith('/') ? rest : `/${rest}`;
  }
  return pathname;
}

/** Minimal types until lib.dom includes Launch Handler. */
type LaunchParams = {
  targetURL?: string;
};

type LaunchQueue = {
  setConsumer(
    consumer: (launchParams: LaunchParams) => void | Promise<void>
  ): void;
};

declare global {
  interface Window {
    launchQueue?: LaunchQueue;
  }
}

/**
 * When the installed PWA is opened from an in-scope link, some clients focus an
 * existing window without navigating. This syncs the SPA router to the launch URL.
 */
export function useLaunchHandler(): void {
  const navigate = useNavigate();

  useEffect(() => {
    const queue = window.launchQueue;
    if (!queue?.setConsumer) return;

    queue.setConsumer((launchParams) => {
      const raw = launchParams.targetURL;
      if (!raw) return;

      let url: URL;
      try {
        url = new URL(raw);
      } catch {
        return;
      }

      if (url.origin !== window.location.origin) return;

      const pathWithinRouter =
        stripBasename(url.pathname) + url.search + url.hash;
      const currentPathWithinRouter =
        stripBasename(window.location.pathname) +
        window.location.search +
        window.location.hash;
      if (pathWithinRouter !== currentPathWithinRouter) {
        navigate(pathWithinRouter);
      }
    });
  }, [navigate]);
}
